import type { IconName } from '@customerio/pluma-icons';
import type { ActionType, TemplateType, CampaignType } from '@/services/auth/types';

export const LABEL = 'Search for anything';

export const COMMAND_DIALOG_QUERY_DEFAULTS = {
  staleTime: 1000 * 30,
  retryOnMount: false,
} as const;

export const SHARED_QUERY_OPTIONS = {
  ...COMMAND_DIALOG_QUERY_DEFAULTS,
  forwardAbortSignal: true,
} as const;

export const CLIENT_SIDE_RESULT_CAP = 50;

/** Kind filter values used by the SegmentedControl when `universal-search-multi` is enabled. */
export const SEARCH_KIND_FILTERS = {
  segments: 'segment',
  campaigns: 'campaign',
  actions: 'action',
  templates: 'template',
  newsletters: 'newsletter',
  goals: 'goal',
  navigation: 'navigation',
} as const;

export type SearchKindFilter = (typeof SEARCH_KIND_FILTERS)[keyof typeof SEARCH_KIND_FILTERS];

export const FACEBOOK_LEAD_ADS_FORM_TYPE = 'facebook_lead_ads';

export const CUSTOM_FORM_TYPE_LABELS: Record<string, string> = {
  custom: 'Custom',
  typeform: 'Typeform',
  jotform: 'Jotform',
  in_app_form: 'In-App Form',
  [FACEBOOK_LEAD_ADS_FORM_TYPE]: 'Facebook Lead Ads',
};

/** Form types that use brand SVG images instead of a Pluma icon. Filenames under /hydra-assets/icons/. */
export const CUSTOM_FORM_TYPE_IMAGE_FILENAMES: Record<string, string> = {
  [FACEBOOK_LEAD_ADS_FORM_TYPE]: 'facebook.svg',
  typeform: 'typeform.svg',
  jotform: 'jotform.svg',
};

/** Form types that map to a Pluma icon name. Falls back to 'form' for unknown types. */
export const CUSTOM_FORM_TYPE_PLUMA_ICONS: Partial<Record<string, IconName>> = {
  custom: 'form',
  in_app_form: 'in-app',
};

export const ACTION_TYPE_ICONS = {
  conditional_wait_action: 'wait',
  delay_seconds_action: 'time-delay',
  delay_time_window_action: 'time-window',
  grace_period_action: 'time-delay',
  email_action: 'email',
  slack_action: 'slack',
  twilio_action: 'sms',
  line_action: 'sms',
  push_action: 'mobile',
  urban_airship_action: 'mobile',
  in_app_action: 'in-app',
  webhook_action: 'webhook',
  attribute_update_action: 'person',
  static_seg_update_action: 'segment-manual',
  collection_query_action: 'collection',
  create_event_action: 'event',
  batch_update_action: 'person',
  split_randomized_action: 'a-b-test',
  conditional_branch_action: 'branch-two',
  multi_split_branch_action: 'branch-three',
  multi_lang_branch_action: 'localization',
  random_cohort_branch_action: 'random-cohort',
  exit_action: 'exit',
  filter_match_delay_action: 'time-delay',
  randomized_delay_action: 'time-delay',
  trigger: 'trigger-event',
  llm_action: 'sparkle',
  inbox_action: 'notifications',
  whatsapp_action: 'sms',
} as const satisfies Record<ActionType, IconName>;

export const TEMPLATE_TYPE_ICONS = {
  email: 'email',
  webhook: 'webhook',
  twilio: 'sms',
  urban_airship: 'mobile',
  slack: 'slack',
  push: 'mobile',
  create_event: 'trigger-event',
  attribute_update: 'person',
  batch_update_not_configured: 'person',
  batch_create_event: 'person',
  batch_attribute_update: 'person',
  in_app: 'in-app',
  in_app_broadcast: 'in-app',
  llm_action_prompt: 'sparkle',
  llm_action_result_schema: 'sparkle',
  line: 'sms',
  inbox: 'notifications',
  whatsapp: 'sms',
  unset: 'newsletter',
} as const satisfies Record<TemplateType, IconName>;

export const TEMPLATE_TYPE_LABELS = {
  email: 'Email',
  webhook: 'Webhook',
  twilio: 'SMS',
  urban_airship: 'Airship',
  slack: 'Slack Message',
  push: 'Push Notification',
  line: 'LINE Message',
  inbox: 'Inbox Message',
  whatsapp: 'WhatsApp',
  in_app: 'In-App Message',
  in_app_broadcast: 'In-App Message',
  create_event: 'Send Event',
  attribute_update: 'Create or Update Person',
  batch_update_not_configured: 'Batch Update',
  batch_create_event: 'Batch Update',
  batch_attribute_update: 'Batch Update',
  llm_action_prompt: 'Run LLM',
  llm_action_result_schema: 'Run LLM',
  unset: 'Newsletter',
} as const satisfies Record<TemplateType, string>;

export const TEMPLATE_TYPE_FILTER_OPTIONS = [
  { label: TEMPLATE_TYPE_LABELS.email, value: 'email' },
  { label: TEMPLATE_TYPE_LABELS.push, value: 'push' },
  { label: TEMPLATE_TYPE_LABELS.in_app, value: 'in_app' },
  { label: TEMPLATE_TYPE_LABELS.twilio, value: 'twilio' },
  { label: TEMPLATE_TYPE_LABELS.slack, value: 'slack' },
  { label: TEMPLATE_TYPE_LABELS.webhook, value: 'webhook' },
  { label: TEMPLATE_TYPE_LABELS.whatsapp, value: 'whatsapp' },
  { label: TEMPLATE_TYPE_LABELS.inbox, value: 'inbox' },
  { label: TEMPLATE_TYPE_LABELS.line, value: 'line' },
  { label: TEMPLATE_TYPE_LABELS.urban_airship, value: 'urban_airship' },
] as const satisfies {
  label: (typeof TEMPLATE_TYPE_LABELS)[Exclude<keyof typeof TEMPLATE_TYPE_LABELS, 'unset'>];
  value: Exclude<TemplateType, 'unset'>;
}[];

export const CAMPAIGN_TYPE_ICONS = {
  seg_attr: 'segment',
  transactional: 'trigger-event',
  behavioral: 'segment',
  triggered_broadcast: 'api-triggered-broadcast',
  date: 'trigger-date',
  form: 'form',
  webhook: 'webhook',
  object: 'object',
  relationship: 'connections',
  none: 'campaigns',
} as const satisfies Record<CampaignType, IconName>;
